import {
  TAttachmentPreviewKind,
  TInvalidAttachmentFile,
} from "../types/candidateAttachment.types";

const SIZE_UNITS = ["B", "KB", "MB", "GB"];
const SIZE_STEP = 1024;

const GENERIC_MIME_TYPE = "application/octet-stream";
const PDF_MIME_TYPE = "application/pdf";
const IMAGE_MIME_PREFIX = "image/";
const VIDEO_MIME_PREFIX = "video/";

// Used only when the backend could not tell the mime type of the file
const PDF_EXTENSIONS = ["pdf"];
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
const VIDEO_EXTENSIONS = ["mp4", "mov", "webm", "ogv", "m4v"];

// Some browsers cancel the download if the object URL is revoked right away
const REVOKE_OBJECT_URL_DELAY_MS = 1000;

/**
 * Same rules as the backend: comma separated, case insensitive, with or
 * without the leading dot, empty values and duplicates dropped.
 */
export const parseAllowedExtensions = (value: string): string[] => {
  const extensions = value
    .split(",")
    .map((extension) => extension.trim().toLowerCase().replace(/^\.+/, ""))
    .filter((extension) => extension !== "");

  return extensions.filter(
    (extension, index) => extensions.indexOf(extension) === index
  );
};

/**
 * Lowercase extension without the dot, or "" when the name has none.
 */
export const getFileExtension = (name: string): string => {
  const dotIndex = name.lastIndexOf(".");
  if (dotIndex === -1) {
    return "";
  }

  return name.slice(dotIndex + 1).toLowerCase();
};

export const findInvalidFiles = (
  files: File[],
  allowedExtensions: string[]
): TInvalidAttachmentFile[] => {
  return files
    .map((file) => ({ file, extension: getFileExtension(file.name) }))
    .filter(
      ({ extension }) =>
        extension === "" || allowedExtensions.indexOf(extension) === -1
    );
};

export const getAttachmentPreviewKind = (
  mimeType: string | null,
  title?: string | null
): TAttachmentPreviewKind | null => {
  const mime = (mimeType ?? "").toLowerCase();

  if (mime !== "" && mime !== GENERIC_MIME_TYPE) {
    if (mime === PDF_MIME_TYPE) {
      return "pdf";
    }
    if (mime.indexOf(IMAGE_MIME_PREFIX) === 0) {
      return "image";
    }
    if (mime.indexOf(VIDEO_MIME_PREFIX) === 0) {
      return "video";
    }

    return null;
  }

  const extension = getFileExtension(title ?? "");
  if (PDF_EXTENSIONS.indexOf(extension) !== -1) {
    return "pdf";
  }
  if (IMAGE_EXTENSIONS.indexOf(extension) !== -1) {
    return "image";
  }
  if (VIDEO_EXTENSIONS.indexOf(extension) !== -1) {
    return "video";
  }

  return null;
};

export const formatFileSize = (bytes: number | null): string => {
  if (bytes === null) {
    return "-";
  }
  if (bytes < SIZE_STEP) {
    return `${bytes} B`;
  }

  let value = bytes;
  let unitIndex = 0;
  while (value >= SIZE_STEP && unitIndex < SIZE_UNITS.length - 1) {
    value = value / SIZE_STEP;
    unitIndex++;
  }

  return `${value.toFixed(1)} ${SIZE_UNITS[unitIndex]}`;
};

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, REVOKE_OBJECT_URL_DELAY_MS);
};
